import { RoutePath } from '~/routing'
import { isCurrentLocationMatching } from '~/utilities/navigation'
import { uuidToB64 } from '~/utilities/uuid'

import { commentsReducerMapObj } from '../comments/reducer'
import type { Middleware } from '../common/actions'
import { currentUserReducerMapObj } from '../currentUser/reducer'
import { fileMetadataReducerMapObj } from '../fileMetadata/reducer'
import { filesReducerMapObj } from '../files/reducer'
import { FileWasRemovedFromJob, FilesWereAddedToJob } from '../job/actions'
import { jobInfoReducerMapObj } from '../jobInfo/reducer'
import { isTimelineJob } from '../jobInfo/selectors'
import { reactionReducerMapObj } from '../reaction/reducer'
import { recentFilesReducerMapObj } from '../recentFiles/recentFilesSlice'
import { timelineReducerMapObj } from '../timeline/reducers'
import { usersReducerMapObj } from '../users/reducer'
import { viewModeReducerMapObj } from '../viewMode/reducer'

import { ChangedCurrentCarouselFile } from './actions'
import { getCurrentViewInfo } from './pureSelectors'
import { carouselViewerReducerMapObj } from './reducer'
import { getCurrentViewJobType, getCurrentViewerNode } from './selectors'

export const carouselViewMiddlewareReducerMapObj = {
    ...carouselViewerReducerMapObj,
    ...commentsReducerMapObj,
    ...currentUserReducerMapObj,
    ...fileMetadataReducerMapObj,
    ...filesReducerMapObj,
    ...jobInfoReducerMapObj,
    ...reactionReducerMapObj,
    ...recentFilesReducerMapObj,
    ...timelineReducerMapObj,
    ...usersReducerMapObj,
    ...viewModeReducerMapObj,
}

type CarouselMiddlewareState = StateOfReducerMapObj<typeof carouselViewMiddlewareReducerMapObj>

export const carouselViewMiddleware: Middleware<CarouselMiddlewareState> =
    (store) => (next) => (action) => {
        const state = store.getState()

        if (
            FileWasRemovedFromJob.match(action) &&
            (isCurrentLocationMatching(RoutePath.getTimelineCarouselPath('([^/]+)')) ||
                isCurrentLocationMatching(RoutePath.getAlbumCarouselPath('[^/]+', '[^/]+')))
        ) {
            const currentViewerNode = getCurrentViewerNode(state)
            if (currentViewerNode && currentViewerNode.file.fileID === action.payload.fileID) {
                const goTo = currentViewerNode.next || currentViewerNode.prev
                if (goTo) {
                    store.dispatch(
                        ChangedCurrentCarouselFile({
                            jobID: currentViewerNode.file.jobID,
                            fileID: goTo.fileID,
                        }),
                    )
                } else {
                    const currentJobType = getCurrentViewJobType(state)
                    const currentView = getCurrentViewInfo(state)
                    if (currentJobType && currentView) {
                        switch (currentJobType) {
                            case 'timeline':
                                location.assign(RoutePath.Timeline)
                                break
                            case 'share':
                                location.assign(RoutePath.getShareReceiverPath(currentView.jobID))
                                break
                            case 'albumPrivate':
                                location.assign(
                                    RoutePath.getLoggedInAlbumB64Path(uuidToB64(currentView.jobID)),
                                )
                                break
                            case 'albumPublic': {
                                location.assign(
                                    RoutePath.getNotLoggedInAlbumPath(uuidToB64(currentView.jobID)),
                                )
                                break
                            }
                        }
                    }
                }
            }
        }

        if (FilesWereAddedToJob.match(action)) {
            // go back to deleted file after 'undo'
            const { prevView } = state.carouselViewer
            if (
                prevView &&
                action.payload.files.length === 1 &&
                prevView.fileID === action.payload.files[0].fileID
            ) {
                store.dispatch(ChangedCurrentCarouselFile(prevView))
            }
        }

        if (ChangedCurrentCarouselFile.match(action)) {
            const { jobID, fileID } = action.payload
            if (
                isTimelineJob(state, jobID) &&
                isCurrentLocationMatching(RoutePath.getTimelineCarouselPath('([^/]+)'))
            ) {
                window.history.replaceState(null, '', RoutePath.getTimelineCarouselPath(fileID))
            } else if (
                isCurrentLocationMatching(RoutePath.getAlbumCarouselPath('[^/]+', '[^/]+'))
            ) {
                window.history.replaceState(
                    null,
                    '',
                    RoutePath.getAlbumCarouselPath(uuidToB64(jobID), fileID),
                )
            }
        }

        return next(action)
    }
